import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Megaphone, Plus, Trash2, Pencil, Save, Eye, EyeOff,
  Upload, X, Link, Type, AlignLeft, Clock, ToggleLeft, ToggleRight,
  Image as ImageIcon, ArrowUp, ArrowDown, ExternalLink, CheckCircle,
} from "lucide-react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";

/* ── helpers ─────────────────────────────────────────────────── */
const uid = () => `slide_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

function Toggle({ checked, onChange, label }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex items-center w-11 h-6 rounded-full transition-colors focus:outline-none ${
        checked ? "bg-indigo-600" : "bg-gray-200"
      }`}
      aria-label={label}
    >
      <span className={`inline-block w-4 h-4 rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-6" : "translate-x-1"
      }`} />
    </button>
  );
}

/* ── ImageUpload ─────────────────────────────────────────────── */
function ImageUpload({ value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver,  setDragOver]  = useState(false);

  const doUpload = async (file) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Solo se permiten imágenes");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const r = await API.post("/upload/popup-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r.data?.data?.url ?? r.data?.url;
      if (url) onChange(url);
      else throw new Error("No URL returned");
    } catch {
      toast.error("Error al subir la imagen");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className={`relative rounded-2xl border-2 border-dashed transition-colors cursor-pointer overflow-hidden
        ${dragOver ? "border-indigo-400 bg-indigo-50" : "border-gray-200 bg-gray-50 hover:border-indigo-300 hover:bg-indigo-50/50"}`}
      style={{ minHeight: 180 }}
      onClick={() => !uploading && inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) doUpload(f); }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) doUpload(f); e.target.value = ""; }}
      />

      {value ? (
        <>
          <img src={value} alt="preview" className="w-full object-cover" style={{ maxHeight: 280 }} />
          <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition flex items-center justify-center opacity-0 hover:opacity-100">
            <div className="flex items-center gap-2 bg-white/90 rounded-xl px-4 py-2 text-sm font-semibold text-gray-800">
              <Upload size={15} /> Cambiar imagen
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
          {uploading ? (
            <>
              <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm font-semibold text-indigo-600">Subiendo imagen…</p>
            </>
          ) : (
            <>
              <div className="w-14 h-14 rounded-2xl bg-indigo-100 flex items-center justify-center mb-3">
                <ImageIcon size={26} className="text-indigo-500" />
              </div>
              <p className="text-sm font-bold text-gray-700">Arrastra o haz clic para subir</p>
              <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP · Máx 5 MB</p>
            </>
          )}
        </div>
      )}
      {uploading && value && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}

/* ── SlideModal ──────────────────────────────────────────────── */
function SlideModal({ slide, onSave, onClose }) {
  const [form, setForm] = useState({
    id:          slide?.id          ?? uid(),
    image:       slide?.image       ?? "",
    title:       slide?.title       ?? "",
    description: slide?.description ?? "",
    buttonText:  slide?.buttonText  ?? "",
    buttonUrl:   slide?.buttonUrl   ?? "",
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isEdit = !!slide;

  const handleSave = () => {
    if (!form.image) { toast.error("Sube una imagen para el slide"); return; }
    onSave(form);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.7)", backdropFilter: "blur(6px)" }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.93, y: 20 }}
        animate={{ opacity: 1, scale: 1,    y: 0  }}
        exit={{   opacity: 0, scale: 0.95,  y: 10 }}
        transition={{ type: "spring", damping: 26, stiffness: 320 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col"
        style={{ maxHeight: "92vh" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-600 to-violet-600">
          <div className="flex items-center gap-2">
            <ImageIcon size={18} className="text-white/80" />
            <h3 className="font-bold text-white">{isEdit ? "Editar slide" : "Nuevo slide"}</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition">
            <X size={15} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Image */}
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              <ImageIcon size={12} /> Imagen / Flyer *
            </label>
            <ImageUpload value={form.image} onChange={v => set("image", v)} />
          </div>

          {/* Title */}
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              <Type size={12} /> Título <span className="text-gray-400 normal-case font-normal">(opcional)</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={e => set("title", e.target.value)}
              placeholder="Ej: ¡Oferta especial de verano!"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition"
            />
          </div>

          {/* Description */}
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              <AlignLeft size={12} /> Descripción <span className="text-gray-400 normal-case font-normal">(opcional)</span>
            </label>
            <textarea
              value={form.description}
              onChange={e => set("description", e.target.value)}
              placeholder="Texto descriptivo que aparece debajo del título…"
              rows={2}
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition resize-none"
            />
          </div>

          {/* Button text + URL */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                <Type size={12} /> Texto del botón
              </label>
              <input
                type="text"
                value={form.buttonText}
                onChange={e => set("buttonText", e.target.value)}
                placeholder="Ver más"
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                <Link size={12} /> URL o ruta
              </label>
              <input
                type="text"
                value={form.buttonUrl}
                onChange={e => set("buttonUrl", e.target.value)}
                placeholder="https://... o /ruta"
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition"
              />
            </div>
          </div>

          {form.buttonUrl && (
            <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-2.5 text-xs text-indigo-700">
              <ExternalLink size={13} />
              <span className="flex-1 truncate">Al hacer clic en la imagen o el botón irá a: <strong>{form.buttonUrl}</strong></span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button onClick={onClose} className="px-5 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100 transition">
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 rounded-xl text-sm font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition flex items-center gap-2"
          >
            <CheckCircle size={15} />
            {isEdit ? "Guardar cambios" : "Agregar slide"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

/* ── SlideCard ───────────────────────────────────────────────── */
function SlideCard({ slide, index, total, onEdit, onDelete, onMoveUp, onMoveDown }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ delay: index * 0.04 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden group hover:shadow-md transition-shadow"
    >
      {/* Thumbnail */}
      <div className="relative bg-gray-100" style={{ aspectRatio: "16/9" }}>
        {slide.image ? (
          <img src={slide.image} alt={slide.title || `Slide ${index + 1}`}
            className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon size={28} className="text-gray-300" />
          </div>
        )}
        {/* Order badge */}
        <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-black/60 text-white text-xs font-black flex items-center justify-center">
          {index + 1}
        </div>
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
          <button
            onClick={() => onEdit(slide)}
            className="w-9 h-9 rounded-xl bg-white flex items-center justify-center shadow hover:bg-indigo-50 transition"
          >
            <Pencil size={15} className="text-indigo-600" />
          </button>
          <button
            onClick={() => onDelete(slide.id)}
            className="w-9 h-9 rounded-xl bg-white flex items-center justify-center shadow hover:bg-red-50 transition"
          >
            <Trash2 size={15} className="text-red-500" />
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="p-3">
        <p className="text-sm font-bold text-gray-800 truncate">
          {slide.title || <span className="text-gray-400 font-normal italic">Sin título</span>}
        </p>
        {slide.buttonUrl ? (
          <p className="text-[11px] text-indigo-600 flex items-center gap-1 mt-1 truncate">
            <Link size={10} /> {slide.buttonUrl}
          </p>
        ) : (
          <p className="text-[11px] text-gray-400 mt-1">Sin enlace</p>
        )}
      </div>

      {/* Reorder buttons */}
      <div className="flex border-t border-gray-100">
        <button
          disabled={index === 0}
          onClick={() => onMoveUp(index)}
          className="flex-1 py-1.5 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition disabled:opacity-30 disabled:cursor-not-allowed text-xs gap-1"
        >
          <ArrowUp size={12} /> Subir
        </button>
        <div className="w-px bg-gray-100" />
        <button
          disabled={index === total - 1}
          onClick={() => onMoveDown(index)}
          className="flex-1 py-1.5 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition disabled:opacity-30 disabled:cursor-not-allowed text-xs gap-1"
        >
          Bajar <ArrowDown size={12} />
        </button>
      </div>
    </motion.div>
  );
}

/* ══════════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
══════════════════════════════════════════════════════════════ */
export default function PopupBannerManager() {
  const [config,  setConfig]  = useState({ active: false, showOnce: true, delaySeconds: 1, layout: 'carousel', slides: [] });
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [modal,   setModal]   = useState(null); // null | "new" | slide object (edit)

  /* Load */
  useEffect(() => {
    API.get("/settings/popup-banner")
      .then(r => {
        const cfg = r.data?.data?.config ?? r.data?.config;
        if (cfg) setConfig(cfg);
      })
      .catch(() => toast.error("Error al cargar configuración"))
      .finally(() => setLoading(false));
  }, []);

  /* Save */
  const save = async () => {
    setSaving(true);
    try {
      await API.put("/settings/popup-banner", config);
      toast.success("Popup guardado correctamente");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  /* Slide operations */
  const addSlide = (slide) => {
    setConfig(c => ({ ...c, slides: [...c.slides, { ...slide, order: c.slides.length }] }));
    setModal(null);
  };
  const updateSlide = (slide) => {
    setConfig(c => ({ ...c, slides: c.slides.map(s => s.id === slide.id ? { ...slide } : s) }));
    setModal(null);
  };
  const deleteSlide = (id) => {
    if (!window.confirm("¿Eliminar este slide?")) return;
    setConfig(c => ({ ...c, slides: c.slides.filter(s => s.id !== id).map((s, i) => ({ ...s, order: i })) }));
  };
  const moveSlide = (index, direction) => {
    const arr = [...config.slides];
    const target = index + direction;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    setConfig(c => ({ ...c, slides: arr.map((s, i) => ({ ...s, order: i })) }));
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4 animate-pulse">
        <div className="h-24 bg-gray-100 rounded-2xl" />
        <div className="h-16 bg-gray-100 rounded-2xl" />
        <div className="grid grid-cols-3 gap-4">
          {[0,1,2].map(i => <div key={i} className="h-48 bg-gray-100 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  const hasSlides = config.slides.length > 0;

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto space-y-6">

      {/* ── Header ──────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shrink-0">
            <Megaphone size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-black text-gray-900">Popup de Bienvenida</h1>
            <p className="text-xs text-gray-500 mt-0.5">Configura el popup que aparece al entrar a la landing page</p>
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 text-white font-bold text-sm hover:bg-indigo-700 transition disabled:opacity-60 shadow-sm"
        >
          {saving
            ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Guardando…</>
            : <><Save size={15} /> Guardar cambios</>
          }
        </button>
      </div>

      {/* ── Status banner ────────────────────────────────────── */}
      <div className={`rounded-2xl border p-4 flex items-center gap-4 transition-colors ${
        config.active
          ? "bg-emerald-50 border-emerald-200"
          : "bg-gray-50 border-gray-200"
      }`}>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
          config.active ? "bg-emerald-500" : "bg-gray-300"
        }`}>
          {config.active ? <Eye size={18} className="text-white" /> : <EyeOff size={18} className="text-white" />}
        </div>
        <div className="flex-1">
          <p className={`font-bold text-sm ${config.active ? "text-emerald-800" : "text-gray-600"}`}>
            {config.active ? "Popup activo — visible en la landing" : "Popup desactivado — no se muestra"}
          </p>
          <p className={`text-xs mt-0.5 ${config.active ? "text-emerald-600" : "text-gray-400"}`}>
            {hasSlides ? `${config.slides.length} slide(s) configurado(s)` : "Agrega al menos un slide para activar"}
          </p>
        </div>
        <Toggle
          checked={config.active}
          onChange={v => setConfig(c => ({ ...c, active: v }))}
          label="Activar popup"
        />
      </div>

      {/* ── Settings strip ───────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Configuración general</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">

          {/* Delay */}
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Clock size={14} className="text-indigo-500" /> Demora de aparición
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0} max={30}
                value={config.delaySeconds}
                onChange={e => setConfig(c => ({ ...c, delaySeconds: Math.max(0, Math.min(30, Number(e.target.value))) }))}
                className="w-20 rounded-xl border border-gray-200 px-3 py-2 text-sm font-bold text-center outline-none focus:ring-2 focus:ring-indigo-300"
              />
              <span className="text-sm text-gray-500">segundos</span>
            </div>
            <p className="text-[11px] text-gray-400">0 = aparece inmediatamente</p>
          </div>

          {/* Show once */}
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <ToggleLeft size={14} className="text-indigo-500" /> Frecuencia de aparición
            </label>
            <div className="flex flex-col gap-2 mt-1">
              {[
                { value: true,  label: "Una vez por sesión" },
                { value: false, label: "Cada vez que entra" },
              ].map(opt => (
                <button
                  key={String(opt.value)}
                  type="button"
                  onClick={() => setConfig(c => ({ ...c, showOnce: opt.value }))}
                  className={`flex items-center gap-2 text-sm px-3 py-2 rounded-xl border transition text-left ${
                    config.showOnce === opt.value
                      ? "border-indigo-400 bg-indigo-50 text-indigo-700 font-semibold"
                      : "border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <span className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    config.showOnce === opt.value ? "border-indigo-500" : "border-gray-300"
                  }`}>
                    {config.showOnce === opt.value && (
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                    )}
                  </span>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Layout */}
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <ImageIcon size={14} className="text-indigo-500" /> Diseño del popup
            </label>
            <div className="flex flex-col gap-2 mt-1">
              {[
                { value: 'carousel', label: "Carrusel (una a una)", desc: "Slides con rotación automática" },
                { value: 'mosaic',   label: "Mosaico (todas juntas)", desc: "Cuadrícula, ajusta tamaño automático" },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setConfig(c => ({ ...c, layout: opt.value }))}
                  className={`flex items-start gap-2 text-sm px-3 py-2 rounded-xl border transition text-left ${
                    (config.layout ?? 'carousel') === opt.value
                      ? "border-violet-400 bg-violet-50 text-violet-700 font-semibold"
                      : "border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <span className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                    (config.layout ?? 'carousel') === opt.value ? "border-violet-500" : "border-gray-300"
                  }`}>
                    {(config.layout ?? 'carousel') === opt.value && (
                      <span className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                    )}
                  </span>
                  <div>
                    <div>{opt.label}</div>
                    <div className="text-[10px] font-normal text-gray-400 mt-0.5">{opt.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Preview note */}
          <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Eye size={14} className="text-indigo-500" /> Vista previa
            </p>
            <div className="bg-gray-50 rounded-xl p-3 text-xs text-gray-500 space-y-1 border border-gray-100">
              <p>• El popup aparece sobre toda la landing</p>
              <p>• Cada imagen puede tener su propio enlace</p>
              {(config.layout ?? 'carousel') === 'carousel'
                ? <p>• Si hay varios slides, rota automáticamente</p>
                : <p>• Todas las imágenes visibles en cuadrícula</p>
              }
              <p>• El usuario puede cerrarlo en cualquier momento</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Slides section ───────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <ImageIcon size={15} className="text-indigo-500" />
            <h2 className="font-bold text-gray-900 text-sm">Slides / Imágenes</h2>
            {hasSlides && (
              <span className="bg-indigo-100 text-indigo-700 text-[10px] font-black px-2 py-0.5 rounded-full">
                {config.slides.length}
              </span>
            )}
          </div>
          <button
            onClick={() => setModal("new")}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition"
          >
            <Plus size={14} /> Agregar slide
          </button>
        </div>

        {!hasSlides ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-6">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
              <ImageIcon size={28} className="text-gray-300" />
            </div>
            <p className="font-bold text-gray-500 text-sm">Sin slides todavía</p>
            <p className="text-xs text-gray-400 mt-1 mb-4">Agrega imágenes para crear tu popup. Cada imagen puede tener su propio enlace.</p>
            <button
              onClick={() => setModal("new")}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-50 text-indigo-700 text-sm font-bold hover:bg-indigo-100 transition border border-indigo-200"
            >
              <Plus size={14} /> Agregar primer slide
            </button>
          </div>
        ) : (
          <div className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence>
              {config.slides.map((slide, i) => (
                <SlideCard
                  key={slide.id}
                  slide={slide}
                  index={i}
                  total={config.slides.length}
                  onEdit={s => setModal(s)}
                  onDelete={deleteSlide}
                  onMoveUp={idx => moveSlide(idx, -1)}
                  onMoveDown={idx => moveSlide(idx, 1)}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* ── Slide modal ──────────────────────────────────────── */}
      <AnimatePresence>
        {modal && (
          <SlideModal
            slide={modal === "new" ? null : modal}
            onSave={modal === "new" ? addSlide : updateSlide}
            onClose={() => setModal(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
